import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { trackBoth } from "@/lib/meta";
import AnnouncementBar from "@/components/store/AnnouncementBar";

const LINKS = [
  { label: "PRODUCT", key: "product" },
  { label: "WHY POSSO", key: "why" },
  { label: "FAQ", key: "faq" },
];

export const goTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

// Minimal mobile-first header: hamburger left, centered brand line in the
// display font, search + bag right (PREORDER pill on desktop).
export default function StoreHeader({ cartCount = 0, onOpenCart }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const nav = (key) => {
    trackBoth("CtaClicked", { origin: `nav_${key}` });
    setMenuOpen(false);
    if (key === "product") {
      navigate("/product");
      return;
    }
    if (pathname === "/") {
      // Let the mobile menu close before smooth-scrolling.
      setTimeout(() => goTo(key), 220);
    } else {
      navigate(`/#${key}`);
    }
  };

  const preorder = () => {
    trackBoth("CtaClicked", { origin: "nav_preorder" });
    setMenuOpen(false);
    if (pathname === "/product") {
      setTimeout(() => goTo("buy"), 220);
    } else {
      navigate("/product");
    }
  };

  const home = () => {
    trackBoth("CtaClicked", { origin: "nav_home" });
    setMenuOpen(false);
    if (pathname === "/") {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 220);
    } else {
      navigate("/");
    }
  };

  const openCart = () => {
    trackBoth("CtaClicked", { origin: "header_bag" });
    if (onOpenCart) onOpenCart();
    else navigate("/product");
  };

  return (
    <>
    <AnnouncementBar />
    <header
      data-testid="store-header"
      className="sticky top-0 z-40 bg-ivory/95 backdrop-blur border-b border-ink/10"
    >
      <div className="px-4 sm:px-5 max-w-6xl mx-auto h-16 sm:h-20 flex items-center justify-between gap-3 relative">
        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
            className="sm:hidden w-9 h-9 inline-flex items-center justify-center rounded-full border-2 border-ink"
          >
            {menuOpen ? (
              <X className="w-4 h-4" strokeWidth={2.5} />
            ) : (
              <Menu className="w-4 h-4" strokeWidth={2.5} />
            )}
          </button>
          <nav className="hidden sm:flex items-center gap-7">
            {LINKS.map((l) => (
              <button
                key={l.key}
                data-testid={`nav-${l.key}`}
                onClick={() => nav(l.key)}
                className="font-display uppercase text-sm tracking-wide hover:opacity-60 transition-opacity duration-150"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>

        <p
          data-testid="header-brand"
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1.5 whitespace-nowrap"
        >
          <span className="font-display uppercase text-3xl sm:text-4xl tracking-wide leading-none">
            POSSO<sup className="text-[0.3em] align-super tracking-normal">TM</sup>
          </span>
          <span className="font-display uppercase text-sm sm:text-base tracking-wide leading-none bg-[#FFD84D] border-2 border-ink rounded-full px-3 py-1 shadow-[2px_2px_0px_#111111]">
            Preorders Now Live
          </span>
        </p>

        <div className="flex items-center gap-2">
          <button
            data-testid="header-bag-button"
            onClick={openCart}
            aria-label="Open bag"
            className="relative w-9 h-9 inline-flex items-center justify-center rounded-full bg-ink text-ivory transition-transform duration-150 active:scale-90"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={2.5} />
            {cartCount > 0 && (
              <span
                data-testid="header-bag-count"
                className="absolute -top-1.5 -right-1.5 w-[18px] h-[18px] rounded-full bg-[#FF6BAA] border-2 border-ivory text-ink font-body font-bold text-[10px] leading-none inline-flex items-center justify-center"
              >
                {cartCount}
              </span>
            )}
          </button>
          <button
            data-testid="header-preorder-button"
            onClick={preorder}
            className="hidden sm:inline-flex min-h-[40px] px-6 rounded-full bg-[#FF6BAA] text-ink font-display text-sm uppercase tracking-wide items-center transition-[transform,filter] duration-150 hover:brightness-95 active:scale-[0.97]"
          >
            Preorder
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            className="sm:hidden border-t-2 border-ink/10 px-4 py-3 flex flex-col gap-0.5"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <button
              data-testid="mobile-nav-home"
              onClick={home}
              className="font-display uppercase text-xl tracking-wide py-2 text-left"
            >
              HOME
            </button>
            {LINKS.map((l) => (
              <button
                key={l.key}
                data-testid={`mobile-nav-${l.key}`}
                onClick={() => nav(l.key)}
                className="font-display uppercase text-xl tracking-wide py-2 text-left"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-nav-preorder"
              onClick={preorder}
              className="mt-2 min-h-[52px] rounded-full bg-[#FF6BAA] text-ink font-display text-lg uppercase tracking-wide"
            >
              Preorder
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
    </>
  );
}
